import Layout from '@/gf/components/Layout'
import Page from '@/gf/components/Page'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import Id from '@/gf/modules/Id'
import UserM from '@/gf/modules/User'
import * as api from '@/suppliers/modules/api'
import { StoreOrdersSearch } from '@/suppliers/modules/api'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ShippingPayload } from '../../types'
import { useMarkVendorDeliveryMutation } from '../_gen/gql'
import Frame from '../components/Frame'
import ShippingForm from '../components/ShippingForm'
import useGqlClient from '../hooks/useGqlClient'
import useSession from '../hooks/useSession'

const GENERIC_SHIPMENT_ERROR = 'Sorry, something went wrong trying to create the shipment.'

// The ship endpoint answers 422 with a reason when the order was cancelled or refunded out
// from under this form (GEA-8837) — the page's state guard only reflects the order as it was
// at load. Show that reason; fall back to the generic message for anything else, including a
// 500 whose body is an HTML error page rather than JSON.
const shipmentErrorMessage = async (response: Response) => {
  try {
    const body = await response.json()

    return typeof body?.error === 'string' && body.error !== ''
      ? body.error
      : GENERIC_SHIPMENT_ERROR
  } catch {
    return GENERIC_SHIPMENT_ERROR
  }
}

const Shipping = () => {
  const { storeOrderId } = useParams<{ storeOrderId: string }>() as { storeOrderId: string }
  const gqlClient = useGqlClient()
  const [markVendorDelivery] = useMarkVendorDeliveryMutation({ client: gqlClient })

  const breadcrumbs = {
    copy: 'Back to Dashboard',
    crumbs: [
      { name: 'Orders', href: '/orders' },
      { name: `Order ${Id.shorten(storeOrderId)}`, href: `/orders/${storeOrderId}` },
      { name: 'Create shipment', href: `/orders/${storeOrderId}/shipping` },
    ],
  }

  const navigate = useNavigate()
  const { user, store } = useSession()
  const [_msgs, msgsMgr] = useMsgs()
  const [storeOrder, setStoreOrder] = useState<StoreOrdersSearch['storeOrders'][number] | null>()
  const [spinnerLive, spinner] = useToggle()

  useEffect(() => {
    api.fetchStoreOrder(storeOrderId).then((so) => {
      if (so && so.gearflowShipping)
        window.location.href = `/orders/${storeOrderId}/configure-shipment`
      setStoreOrder(so)
    })
  }, [storeOrderId])

  // Return a loading page when the storeOrder is undefined
  if (storeOrder === undefined) {
    return (
      <Frame breadcrumbs={breadcrumbs}>
        <Page title="Loading..." />
      </Frame>
    )
  }

  // Not found or not authorized
  // TODO: move auth check to backend
  if (!storeOrder || (!UserM.isAdmin(user) && storeOrder.store.id !== store.id)) {
    return (
      <Frame breadcrumbs={breadcrumbs}>
        <Page title="Oops">
          <Layout>
            <Layout.Section type="full">
              <p>Cannot find that order.</p>
              <p>
                <Link className="text-blue-500 underline" to="/orders">
                  Back to all orders
                </Link>
              </p>
            </Layout.Section>
          </Layout>
        </Page>
      </Frame>
    )
  }

  const onSubmit = async (shippingPayload: ShippingPayload) => {
    spinner.on()

    try {
      const response = await api.createShipment(shippingPayload)

      if (response.ok) {
        navigate(`/orders/${storeOrderId}`)
      } else {
        msgsMgr.add(await shipmentErrorMessage(response), 'negative')
      }
    } catch {
      // The request never produced a response — offline, DNS, a dropped connection.
      msgsMgr.add(GENERIC_SHIPMENT_ERROR, 'negative')
    } finally {
      spinner.off()
    }
  }

  const onSubmitVendorDelivery = (payload: { outForDeliveryAt: DateTime; note: string | null }) => {
    spinner.on()
    return markVendorDelivery({
      variables: {
        ...payload,
        currentUserId: user.id,
        storeOrderId,
      },
    })
      .then(() => {
        navigate(`/orders/${storeOrderId}`)
      })
      .catch(() => {
        msgsMgr.add('Sorry, something went wrong marking Order out for delivery.', 'negative')
      })
      .finally(() => {
        spinner.off()
      })
  }

  return (
    <Frame breadcrumbs={breadcrumbs}>
      {!['PARTIALLY_SHIPPED', 'PROCESSING'].includes(storeOrder.state) && (
        <Page title="Oops">
          <Layout>
            <Layout.Section type="full">
              <p>
                Orders can only be shipped when they are in the &apos;processing&apos; state. This
                order is in the &apos;{storeOrder.state}&apos; state.
              </p>
              <p>
                Please return to the{' '}
                <Link className="text-blue-500 underline" to={`/orders/${storeOrderId}`}>
                  order details page
                </Link>
                .
              </p>
            </Layout.Section>
          </Layout>
        </Page>
      )}
      {['PARTIALLY_SHIPPED', 'PROCESSING'].includes(storeOrder.state) && (
        <Page
          title={`Create shipment (Order ${Id.shorten(storeOrder.id)})`}
          previousPage={`/orders/${storeOrderId}`}
        >
          <ShippingForm
            storeOrder={storeOrder}
            spinnerLive={spinnerLive}
            onSubmit={onSubmit}
            onSubmitVendorDelivery={onSubmitVendorDelivery}
          />
        </Page>
      )}
    </Frame>
  )
}

export default Shipping
